import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button, Input, Spinner } from '../../components/design-system';
import { isValidCode, listVerifiedFactors, needsSecondStep, verifyCode } from '../../lib/mfa';

/** Only paths inside the app: never a full address someone put in the link. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/auth/2fa') ? next : '/';
}

/** The second step of signing in: the 6-digit code from the authenticator app. */
export function TwoFactorChallenge() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, signOut } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNext(params.get('next'));

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    // Nothing to confirm (already done, or 2FA was turned off): carry on.
    void needsSecondStep().then((needed) => { if (!needed) navigate(next, { replace: true }); });
  }, [loading, user, navigate, next]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const factors = await listVerifiedFactors();
      if (factors.length === 0) throw new Error('No authenticator app is set up for this account.');
      await verifyCode(factors[0].id, code);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work.');
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg flex items-center justify-center px-5">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-sm bg-app-card border border-app-border rounded-2xl shadow-sm p-6 flex flex-col gap-4">
        <div>
          <h1 className="font-serif text-2xl text-app-fg">Two-step check</h1>
          <p className="text-sm text-app-muted mt-1">Enter the 6-digit code from your authenticator app.</p>
        </div>
        <Input
          label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          maxLength={7}
          onChange={(e) => setCode(e.target.value)}
        />
        {error && <p role="alert" className="text-sm text-crimson">{error}</p>}
        <Button type="submit" disabled={busy || !isValidCode(code)}>
          {busy ? <Spinner size="sm" className="text-ink" /> : 'Verify'}
        </Button>
        <button type="button" onClick={() => void signOut()} className="text-xs text-app-muted hover:text-app-fg self-center sk-hit">
          Sign out
        </button>
        <p className="text-xs text-app-muted text-center">Lost your phone? Write to Sekretly support and we can reset it for you.</p>
      </form>
    </div>
  );
}
